import { Link } from "react-router-dom";
import { Leaf, Building2, Sparkles, ArrowRight, Phone, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, stagger, item } from "@/components/Reveal";
import { FAQ } from "@/components/FAQ";
import { useLeadModal } from "@/context/LeadModalProvider";
import { RESIDENTIAL, SPECIALTY, IMAGES, CONTACT } from "@/data/site";
import { RecurringPromise } from "@/components/RecurringPromise";
import { usePricing } from "@/context/PricingProvider";
import { useSEO, faqSchema, breadcrumbSchema, webPageSchema } from "@/lib/seo";
import { PAGE_META, SERVICES_FAQS } from "@/data/page_meta";

export default function Services() {
  const pricing = usePricing();
  const { openQuote } = useLeadModal();

  useSEO({
    ...PAGE_META.services,
    jsonLd: [
      webPageSchema({ path: "/services", name: PAGE_META.services.title, description: PAGE_META.services.description, type: "CollectionPage" }),
      breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }]),
      faqSchema(SERVICES_FAQS),
    ],
  });

  return (
    <div data-testid="services-page">
      <section className="pt-36 pb-14 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_10%,rgba(95,165,68,0.10),transparent_45%)]" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-leaf mb-4">Our Services</p>
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold tracking-tighter text-navy leading-[0.92] max-w-4xl">
              Green Cleaning for every space.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
              Residential, commercial, and specialty cleaning across Northern Virginia, DC &amp; Maryland — with Green Cleaning included on every service. Choose a service to see what's included and book online.
            </p>
            <button onClick={openQuote} data-testid="cta-quote-btn" className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-leaf text-white font-medium hover:scale-105 transition-transform mt-8">
              <Phone className="w-4 h-4" /> Get Instant Quote
            </button>
          </Reveal>
        </div>
      </section>

      {/* RESIDENTIAL CARDS */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="flex items-center gap-3 mb-8"><Leaf className="w-6 h-6 text-leaf" /><h2 className="font-heading text-3xl sm:text-4xl font-semibold text-navy">Residential Cleaning</h2></Reveal>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {RESIDENTIAL.map((s) => (
              <motion.div key={s.slug} variants={item}>
                <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`}
                  className="group block bg-white rounded-3xl p-7 border border-border hover:border-leaf/40 hover:-translate-y-1 transition-all duration-300 h-full">
                  <h3 className="font-heading text-xl font-semibold text-navy group-hover:text-leaf transition-colors">{s.name}</h3>
                  <p className="text-muted-foreground mt-2">{s.blurb}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-leaf mt-4">See details &amp; book <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" /></span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* COMMERCIAL + SPECIALTY */}
      <section className="py-16 md:py-20 bg-muted/40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-6">
          <Reveal>
            <Link to="/services/commercial-cleaning" data-testid="service-card-commercial" className="group relative block rounded-3xl overflow-hidden min-h-[320px] h-full">
              <img src={IMAGES.commercial} alt="Commercial cleaning in the DMV" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-transparent" />
              <div className="absolute bottom-0 p-8 text-white">
                <Building2 className="w-7 h-7 text-leaf mb-3" />
                <h3 className="font-heading text-2xl font-semibold mb-2">Commercial Cleaning</h3>
                <p className="text-white/80 mb-3">Offices, multi-family & property management, community facilities, and retail — tailored to your facility.</p>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-leaf">Explore commercial <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" /></span>
              </div>
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-white rounded-3xl p-8 border border-border h-full">
              <div className="flex items-center gap-3 mb-5"><Sparkles className="w-6 h-6 text-leaf" /><h3 className="font-heading text-2xl font-semibold text-navy">Specialty Services</h3></div>
              <div className="space-y-4">
                {SPECIALTY.map((s) => (
                  <div key={s.name} className="border-b border-border pb-4 last:border-0 last:pb-0">
                    <h4 className="font-heading font-semibold text-navy">{s.name}</h4>
                    <p className="text-sm text-muted-foreground mt-1">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* RECURRING PROMISE */}
      <RecurringPromise variant="section" promise={pricing.recurring_promise} />

      {/* SERVICE AREAS CTA */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="rounded-3xl bg-navy text-white p-8 md:p-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 text-leaf mb-3"><MapPin className="w-5 h-5" /><span className="text-xs uppercase tracking-[0.25em]">Service Areas</span></div>
              <h2 className="font-heading text-2xl sm:text-3xl font-semibold">Serving all around the DMV</h2>
              <p className="text-white/70 mt-2 max-w-xl">Alexandria, Arlington, McLean, Tysons, Falls Church, Fairfax, Washington DC, Bethesda, Silver Spring and more.</p>
            </div>
            <Link to="/service-areas" data-testid="link-service-areas" className="shrink-0 group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-leaf text-white font-medium hover:scale-105 transition-transform">
              View service areas <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      <FAQ items={SERVICES_FAQS} title="Services FAQs" />
    </div>
  );
}
